// ONB4-F2 (docs/build-briefs/ONB4-F2-item-kinds-step.md). The one place that decides which selling methods an item kind
// has, shared by Settings (the Add item form and the kind cards) and the "Item kinds" setup step, so the step can never
// disagree with what Settings shows.

import { isCurrentItemKind, type ProductSaleMechanism } from "./api.ts";

/** The slice of an item_kind vocabulary row this decision reads. */
export interface ItemKindMechanismEntry {
  value: string;
  active?: boolean;
  saleMechanism?: string | null | undefined;
  saleMechanisms?: string[];
  policyEnforcementActive?: boolean;
}

/** The fixed kinds' selling methods when a row carries neither a policy set nor a legacy value. 'liquor' sells by the
 * pour and its own legacy column stays NULL by design. */
export const FIXED_ITEM_KIND_SALE_MECHANISMS: Record<string, ProductSaleMechanism[]> = {
  liquor: ["per_ounce"],
  wine: ["per_ounce"],
  beer: ["per_ounce", "whole_unit"],
  cider: ["per_ounce", "whole_unit"],
};

export function itemKindEntrySaleMechanisms(
  itemKind: string,
  entry: ItemKindMechanismEntry | undefined,
): ProductSaleMechanism[] {
  if (!isCurrentItemKind(itemKind)) return [];
  if (entry?.policyEnforcementActive === true) {
    return (entry.saleMechanisms ?? []).filter(
      (m): m is ProductSaleMechanism => m === "per_ounce" || m === "whole_unit",
    );
  }
  if (entry?.saleMechanism === "per_ounce" || entry?.saleMechanism === "whole_unit") {
    return [entry.saleMechanism];
  }
  return FIXED_ITEM_KIND_SALE_MECHANISMS[itemKind] ?? [];
}

/** Setup step "Item kinds": at least one active kind, and every active kind has at least one selling method. A
 * tenant-added kind with none picked is pending (review finding 2026-09-22); inactive kinds are ignored. */
export function itemKindsStepDone(rows: readonly ItemKindMechanismEntry[]): boolean {
  const active = rows.filter((row) => row.active === true && isCurrentItemKind(row.value));
  return (
    active.length > 0 &&
    active.every((row) => itemKindEntrySaleMechanisms(row.value, row).length > 0)
  );
}
